import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { BookOpen, Play, FileText, FileQuestion, CheckCircle, Lock, Mail, ArrowLeft, Loader2 } from 'lucide-react';
import { getCourse, getCourseEnrollment, updateCourseProgress } from '../firebase/courses';
import { FullPageLoader } from '../components/LoadingStates';
import { useErrorHandler } from '../hooks/useErrorHandler';
import { useAuth } from '../hooks/useAuth';

export function CoursePage() {
  const { courseId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { handleError } = useErrorHandler();
  const { isAuthenticated, authLoading } = useAuth();
  
  const [course, setCourse] = useState(null);
  const [enrollment, setEnrollment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [emailSubmitted, setEmailSubmitted] = useState(false);
  const [currentModuleIndex, setCurrentModuleIndex] = useState(0);
  const [currentLessonIndex, setCurrentLessonIndex] = useState(0);
  const [completedLessons, setCompletedLessons] = useState([]);

  useEffect(() => {
    // Wait for auth to load before checking
    if (authLoading) return;
    
    // Check for email in URL params (from email link)
    const emailParam = searchParams.get('email');
    if (emailParam) {
      setEmail(emailParam);
      setEmailSubmitted(true);
    }
    loadCourse();
  }, [courseId, searchParams, authLoading, isAuthenticated]);

  useEffect(() => {
    // Only verify enrollment if not admin and email is provided
    if (emailSubmitted && email && course && !isAuthenticated) {
      verifyEnrollment();
    }
  }, [emailSubmitted, email, course, isAuthenticated]);

  const loadCourse = async () => {
    setLoading(true);
    try {
      const courseData = await getCourse(courseId);
      if (!courseData) {
        navigate('/');
        return;
      }
      // Admins can view draft courses, regular users can only view published
      if (courseData.status !== 'published' && !isAuthenticated) {
        navigate('/');
        return;
      }
      setCourse(courseData);
      // If admin, automatically grant access (skip enrollment check)
      if (isAuthenticated) {
        setEnrollment({ admin: true }); // Set a fake enrollment object for admin
        setEmailSubmitted(true);
      }
    } catch (error) {
      handleError(error, 'Loading course');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const verifyEnrollment = async () => {
    if (!email || !course) return;
    
    setLoading(true);
    try {
      const enrollmentData = await getCourseEnrollment(courseId, email);
      if (enrollmentData) {
        setEnrollment(enrollmentData);
        const completed = enrollmentData.progress?.completedLessons || [];
        setCompletedLessons(completed);
      } else {
        setEnrollment(null);
      }
    } catch (error) {
      handleError(error, 'Verifying enrollment');
    } finally {
      setLoading(false);
    }
  };

  const handleEmailSubmit = (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setEmailSubmitted(true);
    verifyEnrollment();
  };

  const handleCompleteLesson = async (lessonId) => {
    // Admins can't complete lessons (they're just viewing)
    if (isAuthenticated) return;
    if (!email || !courseId) return;
    
    try {
      await updateCourseProgress(courseId, email, lessonId);
      setCompletedLessons(prev => [...prev, lessonId]);
    } catch (error) {
      handleError(error, 'Marking lesson complete');
    }
  };

  if (authLoading || (loading && !course)) {
    return <FullPageLoader message="Loading course..." />;
  }

  if (!course) {
    return null;
  }

  // Email verification form (skip for admins)
  if (!isAuthenticated && (!emailSubmitted || !enrollment)) {
    return (
      <div className="min-h-screen bg-[#fdfbf7] flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl border border-gray-200 p-8">
          <div className="text-center mb-6">
            <BookOpen className="w-16 h-16 text-[#c5a059] mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-800 mb-2">{course.title}</h1>
            <p className="text-gray-600">Enter your email to access this course</p>
          </div>

          <form onSubmit={handleEmailSubmit} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#c5a059] focus:border-[#c5a059]"
                placeholder="your@email.com"
                required
              />
              <p className="text-xs text-gray-500 mt-2">
                Use the email address you used when purchasing this course
              </p>
            </div>

            {emailSubmitted && !enrollment && !loading && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm text-red-600">
                  No enrollment found for this email. Please check your email address or contact support if you believe this is an error.
                </p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading || !email.trim()}
              className="w-full px-6 py-3 bg-[#c5a059] hover:bg-[#b08e4d] text-white rounded-lg font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="animate-spin" size={20} />
                  Verifying...
                </>
              ) : (
                <>
                  <Mail size={20} />
                  Access Course
                </>
              )}
            </button>
          </form>

          <button
            onClick={() => navigate('/')}
            className="mt-4 w-full text-center text-gray-600 hover:text-gray-800 text-sm"
          >
            <ArrowLeft className="inline mr-2" size={16} />
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  // Course player
  const sortedModules = [...(course.modules || [])].sort((a, b) => (a.order || 0) - (b.order || 0));
  const currentModule = sortedModules[currentModuleIndex];
  const sortedLessons = currentModule ? [...(currentModule.lessons || [])].sort((a, b) => (a.order || 0) - (b.order || 0)) : [];
  const currentLesson = sortedLessons[currentLessonIndex];

  const getProgressPercentage = () => {
    const totalLessons = sortedModules.reduce((sum, m) => sum + (m.lessons?.length || 0), 0);
    if (totalLessons === 0) return 0;
    return Math.round((completedLessons.length / totalLessons) * 100);
  };

  return (
    <div className="min-h-screen bg-[#fdfbf7]">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-800"
          >
            <ArrowLeft size={20} />
            <span>Back</span>
          </button>
          <div className="flex-1 mx-8">
            <h1 className="text-xl font-bold text-gray-800 truncate">
              {course.title}
              {isAuthenticated && course.status === 'draft' && (
                <span className="ml-2 text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded">DRAFT</span>
              )}
            </h1>
            {!isAuthenticated && (
              <div className="flex items-center gap-4 mt-1">
                <span className="text-sm text-gray-500">
                  {completedLessons.length} of {sortedModules.reduce((sum, m) => sum + (m.lessons?.length || 0), 0)} lessons completed
                </span>
                <div className="flex-1 max-w-xs bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-[#c5a059] h-2 rounded-full transition-all duration-300"
                    style={{ width: `${getProgressPercentage()}%` }}
                  />
                </div>
                <span className="text-sm font-semibold text-[#c5a059]">{getProgressPercentage()}%</span>
              </div>
            )}
            {isAuthenticated && (
              <div className="text-sm text-blue-600 mt-1">Admin Preview Mode</div>
            )}
          </div>
          <div className="text-sm text-gray-600">
            {isAuthenticated ? 'Admin' : email}
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-8 flex gap-8">
        {/* Sidebar - Modules & Lessons */}
        <aside className="w-80 flex-shrink-0">
          <div className="bg-white rounded-xl shadow-md border border-gray-200 p-4 sticky top-24">
            <h2 className="font-semibold text-gray-800 mb-4">Course Content</h2>
            <div className="space-y-2 max-h-[calc(100vh-200px)] overflow-y-auto">
              {sortedModules.map((module, moduleIdx) => (
                <div key={module.id} className="mb-4">
                  <button
                    onClick={() => {
                      setCurrentModuleIndex(moduleIdx);
                      setCurrentLessonIndex(0);
                    }}
                    className={`w-full text-left p-3 rounded-lg transition ${
                      moduleIdx === currentModuleIndex
                        ? 'bg-[#c5a059] text-white'
                        : 'bg-gray-50 hover:bg-gray-100 text-gray-800'
                    }`}
                  >
                    <div className="font-medium">{module.title}</div>
                    <div className={`text-xs mt-1 ${
                      moduleIdx === currentModuleIndex ? 'text-white/80' : 'text-gray-500'
                    }`}>
                      {module.lessons?.length || 0} lessons
                    </div>
                  </button>
                  
                  {moduleIdx === currentModuleIndex && module.lessons && (
                    <div className="mt-2 ml-4 space-y-1">
                      {[...(module.lessons || [])].sort((a, b) => (a.order || 0) - (b.order || 0)).map((lesson, lessonIdx) => {
                        const isCompleted = completedLessons.includes(lesson.id);
                        const isCurrent = lessonIdx === currentLessonIndex;
                        
                        return (
                          <button
                            key={lesson.id}
                            onClick={() => setCurrentLessonIndex(lessonIdx)}
                            className={`w-full text-left p-2 rounded transition flex items-center gap-2 ${
                              isCurrent
                                ? 'bg-[#c5a059]/20 text-[#c5a059] font-medium'
                                : 'hover:bg-gray-100 text-gray-700'
                            }`}
                          >
                            {isCompleted ? (
                              <CheckCircle size={16} className="text-green-600 flex-shrink-0" />
                            ) : (
                              <div className="w-4 h-4 rounded-full border-2 border-gray-300 flex-shrink-0" />
                            )}
                            <span className="text-sm flex-1 truncate">{lesson.title}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </aside>

        {/* Main Content - Lesson Viewer */}
        <main className="flex-1">
          {currentLesson ? (
            <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
              {/* Lesson Header */}
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-sm text-gray-500">
                    Module {currentModuleIndex + 1} • Lesson {currentLessonIndex + 1}
                  </span>
                </div>
                <h2 className="text-2xl font-bold text-gray-800">{currentLesson.title}</h2>
                {currentLesson.duration > 0 && (
                  <p className="text-sm text-gray-500 mt-1">{currentLesson.duration} minutes</p>
                )}
              </div>

              {/* Lesson Content */}
              <div className="p-8">
                {currentLesson.type === 'video' && currentLesson.content ? (
                  <div className="mb-6">
                    <div className="aspect-video bg-black rounded-lg overflow-hidden">
                      {currentLesson.content.includes('youtube.com') || currentLesson.content.includes('youtu.be') ? (
                        <iframe
                          src={(() => {
                            let embedUrl = currentLesson.content;
                            let videoId = null;
                            
                            // Handle youtube.com/watch?v= format
                            if (embedUrl.includes('youtube.com/watch?v=')) {
                              const match = embedUrl.match(/[?&]v=([a-zA-Z0-9_-]{11})/);
                              if (match && match[1]) {
                                videoId = match[1];
                              }
                            }
                            // Handle youtu.be/ format
                            else if (embedUrl.includes('youtu.be/')) {
                              videoId = embedUrl.split('youtu.be/')[1].split('?')[0].split('&')[0];
                            }
                            // Handle youtube.com/embed/ format (already embedded)
                            else if (embedUrl.includes('youtube.com/embed/')) {
                              const match = embedUrl.match(/embed\/([a-zA-Z0-9_-]{11})/);
                              if (match && match[1]) {
                                videoId = match[1];
                              }
                            }
                            // Try to extract video ID from various YouTube URL formats
                            else {
                              const match = embedUrl.match(/(?:youtube\.com\/|youtu\.be\/)(?:watch\?v=|embed\/|v\/)?([a-zA-Z0-9_-]{11})/);
                              if (match && match[1]) {
                                videoId = match[1];
                              }
                            }
                            
                            if (videoId) {
                              // Build clean embed URL with parameters
                              // Note: YouTube deprecated modestbranding in Aug 2023, so branding cannot be fully hidden
                              const params = new URLSearchParams({
                                'rel': '0', // Don't show related videos from other channels at the end
                                'controls': '1', // Show player controls (play, pause, timeline, quality, fullscreen)
                                'fs': '1', // Allow fullscreen
                                'playsinline': '1', // Play inline on mobile
                                'enablejsapi': '1', // Enable JavaScript API for potential custom controls
                              });
                              return `https://www.youtube.com/embed/${videoId}?${params.toString()}`;
                            }
                            
                            return embedUrl;
                          })()}
                          className="w-full h-full"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                          allowFullScreen
                          title={currentLesson.title}
                        />
                      ) : currentLesson.content.includes('vimeo.com') ? (
                        <iframe
                          src={currentLesson.content.replace('/videos/', '/video/')}
                          className="w-full h-full"
                          allowFullScreen
                          title={currentLesson.title}
                        />
                      ) : (
                        <video
                          src={currentLesson.content}
                          controls
                          className="w-full h-full"
                        />
                      )}
                    </div>
                  </div>
                ) : currentLesson.type === 'pdf' && currentLesson.content ? (
                  <div className="mb-6">
                    <iframe
                      src={currentLesson.content}
                      className="w-full h-[600px] border border-gray-200 rounded-lg"
                      title={currentLesson.title}
                    />
                  </div>
                ) : (
                  <div 
                    className="prose prose-lg max-w-none mb-6"
                    dangerouslySetInnerHTML={{ __html: currentLesson.content || '<p>No content available.</p>' }}
                  />
                )}

                {/* Complete Button (only for enrolled students, not admins) */}
                {!isAuthenticated && !completedLessons.includes(currentLesson.id) && (
                  <button
                    onClick={() => handleCompleteLesson(currentLesson.id)}
                    className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold transition flex items-center gap-2"
                  >
                    <CheckCircle size={20} />
                    Mark as Complete
                  </button>
                )}

                {!isAuthenticated && completedLessons.includes(currentLesson.id) && (
                  <div className="flex items-center gap-2 text-green-600 font-semibold">
                    <CheckCircle size={20} />
                    Lesson Completed
                  </div>
                )}

                {isAuthenticated && (
                  <div className="px-4 py-2 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700">
                    Admin view mode - Progress tracking disabled
                  </div>
                )}
              </div>

              {/* Navigation */}
              <div className="p-6 border-t border-gray-200 flex justify-between">
                <button
                  onClick={() => {
                    if (currentLessonIndex > 0) {
                      setCurrentLessonIndex(currentLessonIndex - 1);
                    } else if (currentModuleIndex > 0) {
                      setCurrentModuleIndex(currentModuleIndex - 1);
                      const prevModule = sortedModules[currentModuleIndex - 1];
                      setCurrentLessonIndex((prevModule.lessons?.length || 1) - 1);
                    }
                  }}
                  disabled={currentModuleIndex === 0 && currentLessonIndex === 0}
                  className="px-6 py-2 border-2 border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
                >
                  Previous
                </button>

                <button
                  onClick={() => {
                    if (currentLessonIndex < sortedLessons.length - 1) {
                      setCurrentLessonIndex(currentLessonIndex + 1);
                    } else if (currentModuleIndex < sortedModules.length - 1) {
                      setCurrentModuleIndex(currentModuleIndex + 1);
                      setCurrentLessonIndex(0);
                    }
                  }}
                  disabled={currentModuleIndex === sortedModules.length - 1 && currentLessonIndex === sortedLessons.length - 1}
                  className="px-6 py-2 bg-[#c5a059] hover:bg-[#b08e4d] text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition"
                >
                  Next
                </button>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-md border border-gray-200 p-12 text-center">
              <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600">No lessons available in this module.</p>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
